import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import { Resolve } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, map, Observable, filter, take, timeout, catchError, of, finalize } from 'rxjs';
import { FlexDashboard } from '../models/dashboard';
import { DashboardFactory } from '../factory/dashboard.factory';
import { UserStore } from 'src/app/shared/user/store/user.store';
import { PermissionCode } from 'src/app/shared/user/models/permission';
import { LoadingService } from 'src/app/shared/services/loading.service';

/**
 * Stellt das Willkommens-Dashboard bereit. Wartet, bis sowohl die nectaOne-Registrierung
 * als auch die User-Permissions definitiv sind, damit Widgets ohne Aufblitzen gefiltert werden.
 * Bei Timeout (5s, z.B. Permissions-Endpoint hängt) wird auf das reduzierte Layout zurückgefallen.
 */
@Injectable({ providedIn: 'root' })
export class WelcomeDashboardResolver implements Resolve<FlexDashboard> {
  private readonly userStore = inject(UserStore);
  private readonly injector = inject(Injector);
  private readonly loadingService = inject(LoadingService);

  resolve(): Observable<FlexDashboard> {
    return runInInjectionContext(this.injector, () => {
      const registered$ = toObservable(this.userStore.isRegisteredNectaOne);
      const permissions$ = toObservable(this.userStore.userPermissions);

      this.loadingService.show();

      return combineLatest([registered$, permissions$]).pipe(
        // Beide Werte muessen aus dem "noch unbekannt"-Zustand raus sein, sonst kann
        // take(1) mit registered=null oder perms=null durchrutschen.
        filter(([registered, perms]) => registered !== null && perms !== null),
        take(1),
        map(([registered]) => {
          const hasReportsAccess = registered === true && this.userStore.hasPermission(PermissionCode.Reports);
          return DashboardFactory.createWelcomeDashboardFlex(hasReportsAccess);
        }),
        timeout({ first: 5000 }),
        catchError(() => of(DashboardFactory.createWelcomeDashboardFlex(false))),
        finalize(() => this.loadingService.hide()),
      );
    });
  }
}
